import { useState, useEffect, useRef } from 'react';
import { BookOpen, Plus, Pencil, Trash2, X, Check, Search, FileText, Video, Mail, Book, Link as LinkIcon, Upload, AlertCircle, Download, Eye, FileSpreadsheet, FileCheck, ChevronUp } from 'lucide-react';
import { Resource, createDocument, getDocuments, updateDocument, deleteDocument, supabase } from '../lib/supabase';
import { Dialog, Disclosure } from '@headlessui/react';
import CustomListbox from '../components/CustomListbox';
import { ChevronDown, ChevronRight } from 'lucide-react';

const RESOURCE_TYPES = [
  { value: 'brief', label: 'Briefs', icon: FileText },
  { value: 'contract', label: 'Contrats', icon: FileCheck },
  { value: 'invoice', label: 'Factures', icon: FileSpreadsheet },
  { value: 'quote', label: 'Devis', icon: FileSpreadsheet },
  { value: 'media_kit', label: 'Kit média', icon: FileText },
  { value: 'email', label: 'Email type', icon: Mail },
  { value: 'training', label: 'Formation interne', icon: Book },
  { value: 'video', label: 'Vidéo', icon: Video },
  { value: 'other', label: 'Autre', icon: LinkIcon },
] as const;

const sanitizeFileName = (fileName: string): string => {
  return fileName
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');
};

export default function Resources() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});
  const [resourcesLimit, setResourcesLimit] = useState<Record<string, number>>({});

  useEffect(() => {
    // Initialiser les limites pour chaque type de ressource
    const initialLimits: Record<string, number> = {};
    RESOURCE_TYPES.forEach(type => {
      initialLimits[type.value] = 3; // Limite initiale de 3 par type
    });
    setResourcesLimit(initialLimits);
  }, []);

  const handleShowMore = (type: string) => {
    setResourcesLimit(prev => ({
      ...prev,
      [type]: resources.filter(r => r.type === type).length // Afficher toutes les ressources de ce type
    }));
    setExpandedTypes(prev => ({
      ...prev,
      [type]: true
    }));
  };

  const handleShowLess = (type: string) => {
    setResourcesLimit(prev => ({
      ...prev,
      [type]: 3 // Revenir à la limite initiale
    }));
    setExpandedTypes(prev => ({
      ...prev,
      [type]: false
    }));
  };

  const [formData, setFormData] = useState<Partial<Resource>>({
    name: '',
    type: 'brief',
    description: '',
    url: '',
  });

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('resources')
        .select('id, name, type, description, url, file_url, file_name, file_type, file_size, agency_id, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      const mappedData: Resource[] = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        type: item.type,
        description: item.description || '',
        url: item.url || '',
        fileUrl: item.file_url || '',
        fileName: item.file_name || '',
        fileType: item.file_type || '',
        fileSize: item.file_size || 0,
        agencyId: item.agency_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      }));

      setResources(mappedData);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.url && !selectedFile && !selectedResource?.fileUrl) {
      setError('Veuillez fournir soit une URL, soit un fichier.');
      return;
    }

    try {
      let fileUrl = '';
      let fileName = '';
      let fileType = '';
      let fileSize = 0;

      if (selectedFile) {
        if (selectedFile.size > 500 * 1024 * 1024) {
          alert('Le fichier est trop volumineux. La taille maximale est de 500 MB.');
          return;
        }

        setIsUploading(true);
        const sanitizedFileName = sanitizeFileName(selectedFile.name);
        const filePath = `resources/${Date.now()}_${sanitizedFileName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('resources')
          .upload(filePath, selectedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('resources')
          .getPublicUrl(filePath);

        fileUrl = publicUrl;
        fileName = selectedFile.name;
        fileType = selectedFile.type;
        fileSize = selectedFile.size;
        setUploadProgress(100);
      }

      // Get current user's agency_id
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error('Erreur d\'authentification');
      }
      if (!user) throw new Error('Vous devez être connecté pour ajouter une ressource');

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('agency_id')
        .eq('id', user.id)
        .maybeSingle();

      if (userError) {
        console.error('User error:', userError);
        throw new Error('Erreur lors de la récupération des informations utilisateur');
      }

      if (!userData || !userData.agency_id) {
        throw new Error('Votre compte n\'est pas associé à une agence. Contactez l\'administrateur.');
      }

      const resourceData: any = {
        name: formData.name,
        type: formData.type,
        description: formData.description || '',
        url: formData.url || '',
        file_url: fileUrl || selectedResource?.fileUrl || '',
        file_name: fileName || selectedResource?.fileName || '',
        file_type: fileType || selectedResource?.fileType || '',
        file_size: fileSize || selectedResource?.fileSize || 0,
        agency_id: userData.agency_id,
      };

      if (selectedResource?.id) {
        const { error: updateError } = await supabase
          .from('resources')
          .update(resourceData)
          .eq('id', selectedResource.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('resources')
          .insert([resourceData]);

        if (insertError) throw insertError;
      }

      await loadResources();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving resource:', error);
      const errorMessage = error?.message || error?.error_description || 'Une erreur est survenue lors de la sauvegarde de la ressource.';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedResource?.id) return;

      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', selectedResource.id);

      if (error) throw error;

      await loadResources();
      setIsDeleteModalOpen(false);
      setSelectedResource(null);
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };

  const handleEdit = (resource: Resource) => {
    setSelectedResource(resource);
    setFormData({
      name: resource.name,
      type: resource.type,
      description: resource.description,
      url: resource.url || '',
    });
    setIsModalOpen(true);
  };

  const handleView = (resource: Resource) => {
    setSelectedResource(resource);
    setIsViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedResource(null);
    setFormData({
      name: '',
      type: 'brief',
      description: '',
      url: '',
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setError('');
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setFormData(prev => ({
      ...prev,
      name: prev.name || file.name.split('.')[0],
    }));
  };

  const formatDate = (date: any) => {
    if (!date) return 'Date inconnue';

    try {
      if (typeof date === 'object' && date !== null && typeof date === 'object' && 'seconds' in date) {
        return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }

      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Date invalide';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = 
      resource.name.toLowerCase().includes(search.toLowerCase()) ||
      resource.description.toLowerCase().includes(search.toLowerCase());
    
    const matchesType = typeFilter === 'all' || resource.type === typeFilter;

    return matchesSearch && matchesType;
  });

  const getResourceTypeIcon = (type: string) => {
    const resourceType = RESOURCE_TYPES.find(t => t.value === type);
    const Icon = resourceType?.icon || LinkIcon;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Ressources</h1>
            <p className="text-white/50">Gérez vos ressources internes</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter une ressource
        </button>
      </div>

      <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Rechercher une ressource..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESOURCE_TYPES.map((resourceType) => {
            const resourcesOfType = resources.filter(r => 
              r.type === resourceType.value && 
              (r.name.toLowerCase().includes(search.toLowerCase()) || 
               r.description.toLowerCase().includes(search.toLowerCase()))
            );
            const displayedResources = resourcesOfType.slice(0, resourcesLimit[resourceType.value] || 3);
            const hasMoreToShow = resourcesOfType.length > (resourcesLimit[resourceType.value] || 3);
            const isExpanded = expandedTypes[resourceType.value] || false;
            const Icon = resourceType.icon;

            return (
              <div key={resourceType.value} className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white/60" />
                    </div>
                    <h3 className="text-lg font-medium text-white">{resourceType.label}</h3>
                  </div>
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, type: resourceType.value }));
                      setIsModalOpen(true);
                    }}
                    className="p-1 text-white/40 hover:text-white/70"
                    title={`Ajouter un ${resourceType.label.toLowerCase()}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2 mb-2">
                  {displayedResources.map((resource) => (
                    <div
                      key={resource.id}
                      className="flex items-center justify-between p-3 bg-white/[0.04] rounded-lg hover:bg-white/[0.08] transition-colors group border border-white/[0.08]"
                    >
                      <div className="flex-1 min-w-0">
                        {resource.fileUrl ? (
                          <a
                            href={resource.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white hover:text-white/80 group relative"
                            title={resource.name}
                          >
                            <span className="truncate">{resource.name}</span>
                            <span className="text-sm text-white/40 flex-shrink-0">
                              ({formatFileSize(resource.fileSize || 0)})
                            </span>
                            <div className="absolute -top-8 left-0 bg-black/80 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {resource.name}
                            </div>
                          </a>
                        ) : resource.url ? (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white hover:text-white/80 group relative"
                            title={resource.name}
                          >
                            <span className="truncate">{resource.name}</span>
                            <LinkIcon className="w-4 h-4 flex-shrink-0" />
                            <div className="absolute -top-8 left-0 bg-black/80 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {resource.name}
                            </div>
                          </a>
                        ) : (
                          <span className="text-white truncate group relative" title={resource.name}>
                            {resource.name}
                            <div className="absolute -top-8 left-0 bg-black/80 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {resource.name}
                            </div>
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleView(resource)}
                          className="p-1 text-white/40 hover:text-white/70 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(resource)}
                          className="p-1 text-white/40 hover:text-white/70 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedResource(resource);
                            setIsDeleteModalOpen(true);
                          }}
                          className="p-1 text-red-600 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {resourcesOfType.length === 0 && (
                    <div className="text-center py-6 text-white/30">
                      Aucun {resourceType.label.toLowerCase()}
                    </div>
                  )}
                </div>
                
                {resourcesOfType.length > 3 && (
                  <div className="text-center">
                    {!isExpanded && hasMoreToShow ? (
                      <button
                        onClick={() => handleShowMore(resourceType.value)}
                        className="text-sm text-white/40 hover:text-white/70 flex items-center justify-center mx-auto gap-1"
                      >
                        <span>Voir {resourcesOfType.length - displayedResources.length} de plus</span>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    ) : isExpanded && (
                      <button
                        onClick={() => handleShowLess(resourceType.value)}
                        className="text-sm text-white/40 hover:text-white/70 flex items-center justify-center mx-auto gap-1"
                      >
                        <span>Réduire</span>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* View Resource Modal */}
      <Dialog
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {selectedResource && (
              <>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-white/40 hover:text-white/70"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                      <div className="text-white/60">
                        {getResourceTypeIcon(selectedResource.type)}
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedResource.name}</h2>
                      <p className="text-white/50">
                        {RESOURCE_TYPES.find(t => t.value === selectedResource.type)?.label}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                    <h3 className="text-lg font-medium text-white mb-2">Description</h3>
                    <p className="text-white/60 whitespace-pre-wrap">{selectedResource.description}</p>
                  </div>

                  <div className="space-y-4">
                    {selectedResource.url && (
                      <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                        <h3 className="text-lg font-medium text-white mb-2">URL</h3>
                        <a
                          href={selectedResource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/60 hover:text-white break-all"
                        >
                          {selectedResource.url}
                        </a>
                      </div>
                    )}

                    {selectedResource.fileUrl && (
                      <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                        <h3 className="text-lg font-medium text-white mb-2">Fichier</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-white/50" />
                            <span className="text-white/60 truncate">{selectedResource.fileName}</span>
                            <span className="text-sm text-white/40">
                              ({formatFileSize(selectedResource.fileSize || 0)})
                            </span>
                          </div>
                          <a
                            href={selectedResource.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white/60 hover:text-white flex-shrink-0"
                          >
                            <Download className="w-5 h-5" />
                            <span>Télécharger</span>
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                      <h3 className="text-lg font-medium text-white mb-2">Informations</h3>
                      <div className="space-y-2 text-white/50">
                        <p>Créé le {formatDate(selectedResource.createdAt)}</p>
                        <p>Dernière modification le {formatDate(selectedResource.updatedAt)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleEdit(selectedResource);
                      }}
                      className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => setIsViewModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14]"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-white/40 hover:text-white/70"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  {selectedResource ? 'Modifier la ressource' : 'Ajouter une ressource'}
                </h2>

                {error && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white/60">
                      Nom de la ressource
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-white/60">
                      Type
                    </label>
                    <CustomListbox
                      value={formData.type}
                      onChange={(value) => setFormData({ ...formData, type: value as Resource['type'] })}
                      options={RESOURCE_TYPES.map(t => ({ value: t.value, label: t.label }))}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-white/60">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                      required
                    />
                  </div>

                  {/* File Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      isDragging
                        ? 'border-white/40 bg-white/[0.08]'
                        : 'border-white/[0.08] bg-white/[0.04] hover:border-white/20'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    />
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-accent-blue" />
                      {selectedFile ? (
                        <div className="text-white">
                          <p className="font-medium">{selectedFile.name}</p>
                          <p className="text-sm text-white/50">
                            {formatFileSize(selectedFile.size)}
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="text-white/60 font-medium">
                            Glissez-déposez un fichier ici ou cliquez pour sélectionner
                          </p>
                          <p className="text-sm text-white/40">
                            PDF, Word, Excel, Images, etc.
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="url" className="block text-sm font-medium text-white/60">
                      URL ou lien (optionnel)
                    </label>
                    <input
                      type="url"
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      className="mt-1 block w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                      placeholder="https://"
                    />
                  </div>

                  {isUploading && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-white/60">Téléchargement en cours...</span>
                        <span className="text-sm text-white/60">{uploadProgress.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-white/[0.08] rounded-full h-2">
                        <div
                          className="bg-white/60 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  {selectedResource ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white">Confirmer la suppression</h3>
              <p className="mt-2 text-sm text-white/50">
                Êtes-vous sûr de vouloir supprimer la ressource "{selectedResource?.name}" ? Cette action est irréversible.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14]"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}